/**
 * Reads `auth_type` from the manifest and dispatches to the right flow.
 *
 * The single most important rule in this file: **the UI branches on auth type,
 * never on the app's name.** That is what makes adding a connector a
 * backend-only change — there is no `if (connector === 'slack')` anywhere in the
 * frontend, and there must never be one.
 */

import { connectionsApi } from '@/core/api/connections.api'
import type {
  AuthType,
  Connection,
  ConnectionSettings,
  ConnectorDetail,
} from '@/core/types/api.types'
import { openAuthPopup } from './popup.handler'

export type FlowKind = 'oauth2' | 'fields' | 'none'

/** Which UI a connector needs, derived purely from its declared auth type. */
export function flowKindFor(authType: AuthType): FlowKind {
  if (authType === 'oauth2') return 'oauth2'
  if (authType === 'none') return 'none'
  // api_key, bearer, basic, jwt, hmac, custom and client_credentials all render
  // the manifest's declared fields and POST them. Client credentials looks like
  // a pasted credential from here on purpose: there is no browser hop, and the
  // token it mints is the server's problem, not the dialog's.
  return 'fields'
}

export interface ConnectRequest {
  connector: ConnectorDetail
  label?: string
  /** Which of the connector's declared auth methods to use. */
  methodId?: string | null
  /** For field-based methods: whatever the manifest declared. */
  fields?: Record<string, unknown>
  /** For `oauth2`: a narrowed subset of the method's declared scopes. */
  scopes?: string[]
  /**
   * Workspace, tags and the two policies chosen before connecting. They travel
   * with the OAuth state too, so an OAuth connection keeps what was chosen
   * rather than silently losing it across the round trip.
   */
  settings?: ConnectionSettings
}

/**
 * Run the connect flow end to end and resolve with the new connection.
 *
 * Callers get one promise regardless of auth type, so the connect button does
 * not need to know which of three flows it just triggered.
 */
export async function connect(request: ConnectRequest): Promise<Connection> {
  const { connector, label, fields, scopes, methodId, settings } = request

  // Branch on the *method's* type, not the connector's: one connector can offer
  // OAuth and a pasted token at the same time.
  const method =
    connector.auth_methods?.find((m) => m.id === methodId) ?? connector.auth_methods?.[0]
  const kind = flowKindFor(method?.type ?? connector.auth.type)

  if (kind === 'oauth2') {
    const { authorize_url, state } = await connectionsApi.startAuthorize(connector.id, {
      label,
      scopes,
      method: method?.id,
      // Sent up front, not after the round trip: when the OAuth app belongs to
      // the connection rather than the server, the authorize URL cannot be built
      // without it.
      fields,
      ...(settings ?? {}),
    })

    const result = await openAuthPopup(authorize_url)

    // Compare the returned state against what we were issued. The backend checks
    // this authoritatively too; failing here just produces a clearer message.
    if (result.state !== state) {
      throw new Error(
        'The authorization response did not match this request. Please start again.',
      )
    }

    return connectionsApi.finishAuthorize(connector.id, result.code, result.state)
  }

  // `none` sends an empty field set — the connector genuinely needs no secret,
  // and the backend still mints a real connection so everything downstream
  // (execution, history, health) behaves identically.
  return connectionsApi.connectWithFields(
    connector.id,
    fields ?? {},
    label,
    method?.id,
    settings,
  )
}

/** One-line summary of what connecting will involve, for the connect dialog. */
export function describeFlow(connector: ConnectorDetail): string {
  switch (flowKindFor(connector.auth.type)) {
    case 'oauth2':
      return `Opens ${connector.name} in a new window to approve access. Nothing is stored until you approve.`
    case 'none':
      return `${connector.name} needs no credentials. Connecting just creates an id you can run actions against.`
    default: {
      const count = connector.auth.fields.length
      return `Paste ${count === 1 ? 'the credential' : `the ${count} values`} below. They are encrypted with AES-256-GCM before being stored and are never returned by the API.`
    }
  }
}
